import { apiClient } from './api-client'
import type { InitData } from './widget-base'

/**
 * The site to send a visitor to when a widget cannot complete an action inline
 * (buying a membership, a gift card, a season signup).
 *
 * Every widget used to build these links as `https://<slug>.holdstyring.dk`.
 * That put our brand in the address bar halfway through the customer's own
 * booking flow, even for studios running their own domain — and it ignored the
 * `base-url` attribute that the API calls already honoured.
 *
 * Resolution order:
 *   1. Verified custom domain from init — the studio's real address.
 *   2. The host the widget actually talks to (`base-url` attribute, or the
 *      tenant subdomain). Keeps links and API on the same origin.
 *   3. `<slug>.holdstyring.dk` as a last resort, matching the old behaviour so
 *      a missing init field can never produce a broken link.
 *
 * Note the split when a studio has BOTH a verified domain and an explicit
 * `base-url`: API traffic follows `base-url` (that is the environment the
 * integrator pointed at), while visitor-facing links follow the custom domain
 * (that is the brand the visitor should see).
 */
export function tenantSiteUrl(initData: Pick<InitData, 'tenantSlug' | 'customDomain'>): string {
  if (initData.customDomain) {
    return `https://${initData.customDomain.replace(/\/$/, '')}`
  }

  const apiBase = apiClient.getBaseUrl()
  if (apiBase) return apiBase.replace(/\/$/, '')

  // tenantSlug is validated as [a-z0-9-]+ upstream; sanitize anyway since this
  // value ends up in an href.
  const safe = String(initData.tenantSlug || '').replace(/[^a-z0-9-]/gi, '')
  return `https://${safe}.holdstyring.dk`
}
